import cookies from 'browser-cookies';
import { __, always, assoc, both, complement, contains, converge, curryN, defaultTo, equals, filter, findIndex, ifElse, insert, invertObj, isNil, join, keys, map, mapObjIndexed, mergeAll, path, pickAll, pipe, prepend, prop, propEq, propOr, replace, sortBy, split, tap, unless, values, when, zipObj } from 'ramda';

var parse = function parse(consent) {
  var parseCategory = pipe(pipe(split(':'), zipObj(['id', 'value']), ifElse(propEq('value', '0'), assoc('value', true), assoc('value', false))), defaultTo({}));

  var getCategoryIndex = pipe(propOr('', 'id'), replace(/[a-zA-Z]/g, ''), Number);

  var parseConsent = pipe(defaultTo(''), split('|'), map(parseCategory), sortBy(getCategoryIndex));

  return parseConsent(consent);
};

var curry2 = curryN(2);
var curry3 = curryN(3);

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var aliasToId = curry2(function (alias, mappings) {
  return pipe(invertObj, prop(alias))(mappings);
});

var idsToAliases = curry2(function (ids, mappings) {
  return pipe(defaultTo({}), keys, map(function (id) {
    var alias = mappings[id];
    var value = ids[id];
    if (alias == null) {
      return;
    }
    return defineProperty({}, alias, value);
  }), filter(Boolean), mergeAll)(ids);
});

var aliasesToIds = curry2(function (aliases, mappings) {
  var iMappings = invertObj(mappings);
  return pipe(defaultTo({}), keys, map(function (alias) {
    var id = iMappings[alias];
    return defineProperty({}, id, aliases[alias]);
  }), mergeAll)(aliases);
});

var idListToAliasList = curry2(function (ids, mappings) {
  return map(prop(__, mappings), defaultTo([], ids));
});

var aliasListToIdList = curry2(function (aliases, mappings) {
  return map(prop(__, invertObj(mappings)), defaultTo([], aliases));
});

var get$1 = function get$$1(mappings, consent, categories) {
  var ids = aliasListToIdList(categories, mappings);

  var defaultOptOuts = pipe(defaultTo([]), map(function (id) {
    return defineProperty({}, id, false);
  }), mergeAll)(ids);

  var parseConsent = pipe(parse, map(function (_ref2) {
    var id = _ref2.id,
        value = _ref2.value;
    return defineProperty({}, id, value);
  }), prepend(defaultOptOuts), mergeAll, unless(always(isNil(categories)), pickAll(ids)));

  var getOptOuts = pipe(parseConsent, idsToAliases(__, mappings));

  return getOptOuts(consent);
};

var set$1 = function set(mappings, consent, categories) {
  var idCategories = aliasesToIds(categories, mappings);

  var formatCategories = pipe(aliasesToIds(__, mappings), mapObjIndexed(function (category, id) {
    return pipe(prop(id), ifElse(equals(true), always(0), always(1)), function (value) {
      return id + ':' + value;
    })(idCategories);
  }), values, join('|'));

  return pipe(get$1, formatCategories)(mappings, consent);
};

var getByOrder = function getByOrder(consent, categories) {
  var parsed = parse(consent);
  var i = findIndex(function (category) {
    return category.id === 'c1';
  }, parsed);
  var categoriesWithDefault = when(both(always(i >= 0), complement(contains('default'))), insert(i, 'default'))(categories);

  return zipObj(categoriesWithDefault, parsed);
};

var consent = function consent() {
  var mappings = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var get = function get(categories, consent) {
    return get$1(mappings, consent, categories);
  };
  var set = function set(categories, consent) {
    return set$1(mappings, consent, categories);
  };
  var getByOrder$$1 = function getByOrder$$1(categories, consent) {
    return getByOrder(consent, categories);
  };

  return {
    get: get,
    set: set,
    getByOrder: getByOrder$$1
  };
};

var identityConsent = function identityConsent(mappings, consentStr) {
  var str = consentStr;
  var o = consent(mappings);

  var get = function get(categories) {
    return o.get(categories, str);
  };
  var set = function set(categories) {
    str = o.set(categories, str);
    return str;
  };
  var getByOrder = function getByOrder(categories) {
    return o.getByOrder(categories, str);
  };

  return {
    get: get,
    set: set,
    getByOrder: getByOrder
  };
};

var CONSENT_COOKIE = 'OPTOUTMULTI';

var getDomain = converge(defaultTo, [path(['location', 'hostname']), path(['utag', 'cfg', 'domain'])]);

var cookieConsent = function cookieConsent(cookies$$1, window) {
  return function () {
    var mappings = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var cookieOptions = arguments[1];

    var o = consent(mappings);

    var getFromCookie = function getFromCookie() {
      return cookies$$1.get(CONSENT_COOKIE);
    };
    var writeToCookie = function writeToCookie(value) {
      var domain = getDomain(window);
      var opts = Object.assign({ domain: domain }, cookieOptions);
      cookies$$1.set(CONSENT_COOKIE, value, opts);
    };

    var get = function get(categories) {
      return pipe(getFromCookie, function (consent$$1) {
        return o.get(categories, consent$$1);
      })(null);
    };
    var set = function set(categories) {
      return pipe(getFromCookie, function (consent$$1) {
        return o.set(categories, consent$$1);
      }, tap(writeToCookie))(null);
    };
    var getByOrder = function getByOrder(categories) {
      return pipe(getFromCookie, function (consent$$1) {
        return o.getByOrder(categories, consent$$1);
      })(null);
    };

    return {
      get: get,
      set: set,
      getByOrder: getByOrder
    };
  };
};

var cookieConsent$1 = cookieConsent(cookies, window);

export { consent, identityConsent, cookieConsent$1 as cookieConsent };
